import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner } from "reactstrap";
import DataTable from "react-data-table-component";
import {
  Add,
  ViewPaymentSettingTitle,
  PaymentSettingTitle,
} from "../../../Utils/Constants";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import { baseUrl, mediaUrl } from "../../../Utils/FileUpload";
import { PaymentSetting } from "../../../Types/Tables.type";
import FilterComponent from "../../../Components/Tables/DataTables/Common/FilterComponent";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn } from "../../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../Context/AuthContext";

export default function ViewPaymentSetting() {
  const [filterText, setFilterText] = useState("");
  const [filteredItems, setFilteredItems] = useState<PaymentSetting[]>([]);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { hasPermission } = useAuth();
  const [disabledEnable, setDisabledEnable] = useState<string | null>(null);
  const [disableAllEnable, setDisableAllEnable] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${baseUrl}payment_setting/getall`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data: PaymentSetting[] = response.data;
        setFilteredItems(data);
        // console.log(data)
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refresh]);

  const handleAdd = () => {
    navigate("/add_payment_setting");
  };

  const filteredData = filteredItems.filter((item: PaymentSetting) => {
    const search = filterText.toLowerCase();
    return (
      item.gateway_name.toLowerCase().includes(search) 
    );
  });

  const handleEdit = (id: string) => {
    navigate("/edit_payment_setting", { state: { id: id } });
  };

  const toggleEnable = (id: string, currentStatus: number) => {
    setDisabledEnable(id); // reuse this state if needed
    setDisableAllEnable(true);

    const confirmToggle = () => {
      toast.warn(
        <div>
          <div>
            Are you sure you want to{" "}
            <strong>{currentStatus === 1 ? "disable" : "enable"}</strong> this
            payment setting?
          </div>
          <div className="d-flex justify-content-end mt-2">
            <Button
              color={currentStatus === 1 ? "danger" : "success"}
              onClick={() => confirmToggleEnable(id, currentStatus)}
            >
              Yes
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setDisabledEnable(null);
                setDisableAllEnable(false);
                toast.dismiss();
              }}
              className="ms-2"
            >
              Cancel
            </Button>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          onClose: () => {
            setDisabledEnable(null);
            setDisableAllEnable(false);
          },
        }
      );
    };

    const confirmToggleEnable = async (id: string, currentStatus: number) => {
      setDisabledEnable(null);
      setDisableAllEnable(false);
      toast.dismiss();

      try {
        const newStatus = currentStatus === 1 ? 0 : 1;

        await axios.post(
          `${baseUrl}payment_setting/toggle_active`,
          { id, is_active: newStatus },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        toast.success(
          `Payment setting ${
            newStatus === 1 ? "enabled" : "disabled"
          } successfully!`,
          {
            position: "top-right",
            autoClose: 1000,
            closeOnClick: true,
            onClose: () => {
              setRefresh((prev) => prev + 1);
            },
          }
        );
      } catch (error) {
        console.error("Error toggling payment setting:", error);
        toast.error("Error updating enable status", {
          position: "top-right",
          autoClose: 1000,
        });
      }
    };

    confirmToggle();
  };

  // Define columns with actions
  const paymentSettingTableColumns = [
    {
      name: "Gateway Name",
      selector: (row: PaymentSetting) => row.gateway_name,
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row: PaymentSetting) => (
        <div className="d-flex justify-content-between">
          {hasPermission("payment-setting.edit") && (
            <Button
              color="primary"
              size="sm"
              onClick={() => handleEdit(row.id)}
              style={{
                marginRight: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Edit"
            >
              <i className="icon-pencil-alt" />
            </Button>
          )}
          {hasPermission("payment-setting.enable_disable") && (
            <Button
              color={row.is_active ? "success" : "secondary"}
              size="sm"
              onClick={() => toggleEnable(row.id, row.is_active)}
              disabled={disabledEnable === row.id || disableAllEnable}
              style={{
                marginRight: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title={row.is_active ? "Disable" : "Enable"}
            >
              <i
                className={`fa ${row.is_active ? "fa-check-circle" : "fa-ban"}`}
              />
            </Button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      // button: true,
      width: "10%",
    },
  ];

  // Show a loading spinner while data is being fetched
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }
  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };
  return (
    <>
      <Breadcrumbs
        pageTitle={ViewPaymentSettingTitle}
        parent={PaymentSettingTitle}
        title={ViewPaymentSettingTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            {hasPermission("payment-setting.add") && (
              <Col xs={12} className="mt-2 d-flex justify-content-end mb-3">
                <Btn color="primary" onClick={handleAdd}>
                  {Add}
                </Btn>
              </Col>
            )}
            {hasPermission("payment-setting.view") && (
              <>
                <div className="mb-3">
                  <FilterComponent
                    onFilter={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFilterText(e.target.value)
                    }
                    filterText={filterText}
                  />
                </div>
                <div className="table-responsive">
                  <DataTable
                    data={filteredData}
                    columns={paymentSettingTableColumns}
                    pagination
                    highlightOnHover
                    striped
                    customStyles={customStyles}
                  />
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Col>
      <ToastContainer />
    </>
  );
}
